import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type DependencyList,
} from "react";

type Status = "idle" | "loading" | "success" | "error";

export interface UseAsyncResult<T> {
  data: T | null;
  error: Error | null;
  status: Status;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

/**
 * useAsync - A generic hook for asynchronous data fetching.
 *
 * @param queryFn - Async function to fetch data. Accepts an AbortSignal.
 * @param dependencies - Dependency list to determine when to refetch.
 * @param delay - Optional debounce delay in milliseconds.
 * @returns An object containing the fetch state and a refetch function.
 */
function useAsync<T>(
  queryFn: (signal: AbortSignal) => Promise<T>,
  dependencies: DependencyList = [],
  delay = 0,
): UseAsyncResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<Status>("idle");

  // Store the current AbortController so we can cancel in-flight requests.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Always use the latest version of queryFn.
  const latestQueryFn = useRef(queryFn);
  useEffect(() => {
    latestQueryFn.current = queryFn;
  }, [queryFn]);

  useEffect(() => {
    // Abort any previous request.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    const fetchData = async () => {
      setStatus("loading");
      setError(null);
      setData(null);

      try {
        console.log("fetching data: ", latestQueryFn.current);
        const result = await latestQueryFn.current(signal);
        if (!signal.aborted) {
          setData(result);
          setStatus("success");
        }
      } catch (err) {
        if (!signal.aborted) {
          setError(err instanceof Error ? err : new Error("An error occurred"));
          setStatus("error");
        }
      }
    };

    let timerId: ReturnType<typeof setTimeout> | null = null;
    if (delay > 0) {
      timerId = setTimeout(fetchData, delay);
    } else {
      fetchData();
    }

    // Cleanup: clear debounce timer and abort request.
    return () => {
      if (timerId) clearTimeout(timerId);
      controller.abort();
    };
  }, [...dependencies, delay]);

  return {
    data,
    error,
    status,
    isLoading: status === "loading",
    isSuccess: status === "success",
    isError: status === "error",
  };
}

export default useAsync;
